import { useState } from "react";
import { FileJson, Plus } from "lucide-react";
import { Alert, Button, ConfirmModal, Page, PageHeader, Tabs } from "@/components/ui";
import { DatasetList } from "@/components/evaluation/DatasetList";
import { DatasetImport } from "@/components/evaluation/DatasetImport";
import { DatasetEditor } from "@/components/evaluation/DatasetEditor";
import { useDatasets } from "@/components/evaluation/hooks";
import { API_BASE, type Dataset, type DatasetQuestion } from "@/components/evaluation/types";

type View = "list" | "edit" | "import";

export default function DatasetsPage() {
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { datasets, loading: loadingDatasets, reload: loadDatasets } = useDatasets(setError);

  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [editedDataset, setEditedDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<View>("list");
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: "dataset" | "question"; id: string | number } | null>(null);

  const flashSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const loadDataset = async (version: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/evaluation/datasets/${version}`);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      setSelectedDataset(data);
      setEditedDataset(JSON.parse(JSON.stringify(data))); // Deep copy
      setView("edit");
    } catch (err) {
      console.error("Failed to load dataset:", err);
      setError("Vragenlijst kon niet worden geladen");
    } finally {
      setLoading(false);
    }
  };

  const saveDataset = () => {
    if (!editedDataset) return;
    // Overwriting an existing version needs confirmation
    const isOverwriting = datasets.some((ds) => ds.version === editedDataset.version);
    if (isOverwriting) {
      setShowSaveConfirmModal(true);
    } else {
      performSave();
    }
  };

  const performSave = async () => {
    if (!editedDataset) return;

    setShowSaveConfirmModal(false);
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const isExisting = datasets.some((ds) => ds.version === editedDataset.version);

      let response: Response;
      if (isExisting) {
        // PUT for existing datasets
        response = await fetch(`${API_BASE}/api/evaluation/datasets/${editedDataset.version}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedDataset),
        });
      } else {
        // POST /import for new datasets
        response = await fetch(`${API_BASE}/api/evaluation/datasets/import`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedDataset),
        });
      }

      const result = await response.json();
      if (result.error) {
        setError(result.error);
        return;
      }

      flashSuccess(`Vragenlijst ${editedDataset.version} is opgeslagen.`);
      loadDatasets();
    } catch (err) {
      console.error("Failed to save dataset:", err);
      setError("Vragenlijst kon niet worden opgeslagen");
    } finally {
      setLoading(false);
    }
  };

  const importDataset = async (jsonContent: string) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const parsed = JSON.parse(jsonContent);

      const response = await fetch(`${API_BASE}/api/evaluation/datasets/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed),
      });

      const result = await response.json();
      if (result.error) {
        setError(result.error);
        return;
      }

      flashSuccess(`Vragenlijst ${result.version} is geïmporteerd.`);
      loadDatasets();
      setView("list");
    } catch (err: any) {
      console.error("Failed to import dataset:", err);
      setError(`Importeren mislukt: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const performDelete = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    setError(null);

    try {
      if (deleteTarget.type === "dataset") {
        const response = await fetch(`${API_BASE}/api/evaluation/datasets/${deleteTarget.id}`, { method: "DELETE" });
        const result = await response.json();

        if (result.error) {
          setError(result.error);
          return;
        }

        setSuccessMessage(`Vragenlijst ${deleteTarget.id} is verwijderd.`);
        loadDatasets();
        if (selectedDataset?.version === deleteTarget.id) {
          setSelectedDataset(null);
          setEditedDataset(null);
          setView("list");
        }
      } else if (deleteTarget.type === "question") {
        if (!editedDataset) return;
        const questions = editedDataset.questions.filter((_, i) => i !== deleteTarget.id);
        setEditedDataset({ ...editedDataset, questions, total_questions: questions.length });

        if (editingQuestionIndex === deleteTarget.id) {
          setEditingQuestionIndex(null);
        }
        setSuccessMessage("Vraag is verwijderd.");
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Failed to delete:", err);
      setError("Verwijderen mislukt");
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const updateQuestion = (index: number, field: keyof DatasetQuestion, value: any) => {
    if (!editedDataset) return;
    const questions = [...editedDataset.questions];
    questions[index] = { ...questions[index], [field]: value };
    setEditedDataset({ ...editedDataset, questions });
  };

  const addQuestion = () => {
    if (!editedDataset) return;

    const newQuestion: DatasetQuestion = {
      id: `q${String(editedDataset.questions.length + 1).padStart(3, "0")}`,
      domain: "",
      question: "",
      difficulty: "medium",
      category: "",
      expected_tables: [],
      reference_sql: "",
      explanation: "",
      human_readable_answer: "",
    };

    const questions = [...editedDataset.questions, newQuestion];
    setEditedDataset({ ...editedDataset, questions, total_questions: questions.length });
    setEditingQuestionIndex(questions.length - 1);
  };

  const createNewDataset = () => {
    // Suggest the next version number based on existing datasets
    let suggestedVersion = "1.0";
    const versionNumbers = datasets.map((ds) => parseFloat(ds.version)).filter((n) => !isNaN(n));
    if (versionNumbers.length > 0) {
      suggestedVersion = (Math.max(...versionNumbers) + 1).toFixed(1);
    }

    setSelectedDataset(null);
    setEditedDataset({
      version: suggestedVersion,
      name: "Nieuwe vragenlijst",
      description: "",
      created_date: new Date().toISOString().split("T")[0],
      domains: [],
      total_questions: 0,
      questions: [],
    });
    setView("edit");
  };

  const cancelEdit = () => {
    setView("list");
    setSelectedDataset(null);
    setEditedDataset(null);
  };

  const tabs: { value: View; label: string; count?: number }[] = [
    { value: "list", label: "Overzicht", count: datasets.length },
    { value: "import", label: "JSON importeren" },
  ];
  if (selectedDataset || editedDataset) {
    tabs.splice(1, 0, { value: "edit", label: `Bewerken: ${(editedDataset ?? selectedDataset)!.version}` });
  }

  const deleteQuestionId = deleteTarget?.type === "question" ? editedDataset?.questions[deleteTarget.id as number]?.id : undefined;

  return (
    <Page>
      <PageHeader
        title="Vragenlijsten"
        description="Beheer de vragenlijsten met referentie-SQL waartegen agents worden geëvalueerd."
        actions={
          view === "list" && (
            <>
              <Button variant="outline" onClick={() => setView("import")}>
                <FileJson className="h-4 w-4" />
                JSON importeren
              </Button>
              <Button variant="brand" onClick={createNewDataset}>
                <Plus className="h-4 w-4" />
                Nieuwe vragenlijst
              </Button>
            </>
          )
        }
      />

      <div className="mb-4 space-y-3">
        {error && (
          <Alert tone="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && <Alert tone="success">{successMessage}</Alert>}
      </div>

      <Tabs value={view} onChange={setView} items={tabs} className="mb-6" />

      {view === "list" && (
        <DatasetList
          datasets={datasets}
          loading={loadingDatasets}
          busy={loading}
          onEdit={loadDataset}
          onDelete={(version) => setDeleteTarget({ type: "dataset", id: version })}
          onCreate={createNewDataset}
        />
      )}

      {view === "import" && <DatasetImport loading={loading} onImport={importDataset} onCancel={() => setView("list")} />}

      {view === "edit" && editedDataset && (
        <DatasetEditor
          dataset={editedDataset}
          onChange={setEditedDataset}
          loading={loading}
          canReset={selectedDataset !== null}
          editingQuestionIndex={editingQuestionIndex}
          onToggleQuestion={(index) => setEditingQuestionIndex(editingQuestionIndex === index ? null : index)}
          onUpdateQuestion={updateQuestion}
          onAddQuestion={addQuestion}
          onRemoveQuestion={(index) => setDeleteTarget({ type: "question", id: index })}
          onDeleteAllQuestions={() => setShowDeleteAllModal(true)}
          onSave={saveDataset}
          onReset={() => selectedDataset && setEditedDataset(JSON.parse(JSON.stringify(selectedDataset)))}
          onCancel={cancelEdit}
        />
      )}

      {/* Save: overwrite confirmation */}
      <ConfirmModal
        open={showSaveConfirmModal && !!editedDataset}
        title="Vragenlijst bestaat al"
        message={
          <>
            <p>
              Vragenlijst <span className="font-mono font-semibold text-foreground">"{editedDataset?.version}"</span> bestaat al.
            </p>
            <p className="mt-2">Wil je deze overschrijven, of de versie wijzigen om een nieuwe vragenlijst te maken?</p>
          </>
        }
        confirmLabel="Overschrijven"
        cancelLabel="Versie wijzigen"
        loading={loading}
        onConfirm={performSave}
        onCancel={() => setShowSaveConfirmModal(false)}
      />

      {/* Delete dataset / question confirmation */}
      <ConfirmModal
        open={!!deleteTarget}
        tone="danger"
        title={deleteTarget?.type === "dataset" ? "Vragenlijst verwijderen" : "Vraag verwijderen"}
        message={
          <>
            <p>
              {deleteTarget?.type === "dataset" ? (
                <>
                  Weet je zeker dat je vragenlijst <span className="font-mono font-semibold text-foreground">"{String(deleteTarget.id)}"</span> wilt verwijderen?
                </>
              ) : (
                <>
                  Weet je zeker dat je vraag <span className="font-mono font-semibold text-foreground">{deleteQuestionId}</span> wilt verwijderen?
                </>
              )}
            </p>
            <p className="mt-2 text-xs">Dit kan niet ongedaan worden gemaakt.</p>
          </>
        }
        confirmLabel="Verwijderen"
        loading={loading}
        onConfirm={performDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Delete all questions confirmation (was window.confirm) */}
      <ConfirmModal
        open={showDeleteAllModal}
        tone="danger"
        title="Alle vragen verwijderen"
        message="Weet je zeker dat je alle vragen wilt verwijderen? Dit kan niet ongedaan worden gemaakt."
        confirmLabel="Alles verwijderen"
        onConfirm={() => {
          if (editedDataset) setEditedDataset({ ...editedDataset, questions: [], total_questions: 0 });
          setShowDeleteAllModal(false);
        }}
        onCancel={() => setShowDeleteAllModal(false)}
      />
    </Page>
  );
}
